import { useMemo, type ReactNode } from "react";

import { useScheduleChange } from "@/domains/pm/hooks/useScheduleChange";
import { ScheduleChangeContext } from "./scheduleChangeContext";
import { ScheduleChangeDialog } from "./ScheduleChangeDialog";

/**
 * Makes the cascade reachable from every date control on the page, and owns
 * the one confirmation dialog they all share.
 *
 * A context rather than a prop because the controls are scattered — a list
 * cell, a popover in the detail panel, a context-menu submenu, a Gantt bar —
 * and threading one callback through all of them would mean touching every
 * component in between for a behaviour none of them care about.
 */
export function ScheduleChangeProvider({
  buildingId,
  children,
}: {
  buildingId: number;
  children: ReactNode;
}) {
  const { requestChange, applyWithCascade, hasDependents, pending, applying, resolve, cancel } =
    useScheduleChange(buildingId);

  const api = useMemo(
    () => ({ request: requestChange, hasDependents, applyWithCascade }),
    [requestChange, hasDependents, applyWithCascade]
  );

  return (
    <ScheduleChangeContext.Provider value={api}>
      {children}
      <ScheduleChangeDialog
        pending={pending}
        applying={applying}
        onResolve={(cascade) => void resolve(cascade)}
        onCancel={cancel}
      />
    </ScheduleChangeContext.Provider>
  );
}
